/**
 * OtterAuth (04-architecture § 11, server 06-auth).
 *
 * OtterAuth is a **custom REST API**, not OIDC: opaque `ot_…` tokens, no JWKS,
 * nothing to verify locally. Never reach for an OIDC library.
 *
 * The two detours — TOTP required and challenge required — can come back from
 * **every** login call, which is why all entry points funnel through
 * `handleAuthResult`.
 */

import type { AuthErrorKind } from './errors.js';

export type AuthState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'codeSent'; email: string; resendAt: number }
  | { status: 'verifying' }
  | { status: 'totpRequired'; token: string }
  | { status: 'challengeRequired'; token: string }
  | { status: 'authenticated'; token: string; userId: string }
  | { status: 'error'; kind: AuthErrorKind };

export type AuthEvent =
  | { type: 'send_otp'; email: string }
  | { type: 'otp_sent'; email: string; now: number }
  | { type: 'verify' }
  | { type: 'result'; result: AuthResult }
  | { type: 'failed'; kind: AuthErrorKind }
  | { type: 'reset' };

/**
 * What an OtterAuth login call can answer with.
 *
 * The `token` of the two detours is *not* a session token and the two are not
 * interchangeable: a TOTP token is redeemed by `authenticateTotp`, a challenge
 * token by `verifyChallenge`. The variant tag says which, so both are simply
 * `token` rather than a shared name that invites passing one where the other
 * belongs.
 */
export type AuthResult =
  | { kind: 'session'; sessionToken: string; userId: string }
  | { kind: 'totp_required'; token: string }
  | { kind: 'challenge'; token: string };

/**
 * The resend cooldown of 06 § 5.2 and 09 § 4.1. Distinct from OtterAuth's own
 * limit of five codes per address per ten minutes, which surfaces as
 * `otp_rate_limited` and disables the link far longer.
 */
export const OTP_RESEND_COOLDOWN_MS = 30_000;

export const initialAuthState: AuthState = { status: 'idle' };

/** Pure reducer, unit-tested without a network (12 § 2). */
export function authReducer(state: AuthState, event: AuthEvent): AuthState {
  switch (event.type) {
    case 'send_otp':
      return { status: 'sending' };
    case 'otp_sent':
      return { status: 'codeSent', email: event.email, resendAt: event.now + OTP_RESEND_COOLDOWN_MS };
    case 'verify':
      return { status: 'verifying' };
    case 'result':
      return handleAuthResult(event.result);
    case 'failed':
      return { status: 'error', kind: event.kind };
    case 'reset':
      return initialAuthState;
  }
}

/** The single place that interprets a login answer (§ 11.2). */
export function handleAuthResult(result: AuthResult): AuthState {
  switch (result.kind) {
    case 'session':
      return { status: 'authenticated', token: result.sessionToken, userId: result.userId };
    case 'totp_required':
      return { status: 'totpRequired', token: result.token };
    case 'challenge':
      // Should not occur with fingerprint enforcement off (A-035); handled
      // generically with a code input rather than left to crash.
      return { status: 'challengeRequired', token: result.token };
  }
}

/** § 11.3 — the web OAuth callback carries the temp token in the fragment. */
export function readOAuthFragment(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.get('token');
}

/**
 * The fragment must be scrubbed immediately: a token in the address bar ends up
 * in history, in screenshots and in the referrer.
 */
export function scrubOAuthFragment(replaceState: (url: string) => void, path = '/auth/callback'): void {
  replaceState(path);
}

export * from './errors.js';
export * from './client.js';
